import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../../utils/axios';
import { FiUpload, FiSave, FiX } from 'react-icons/fi';

const PortfolioForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    title: '',
    categoryId: '',
    categoryName: '',
    image: null
  });
  const [preview, setPreview] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isNewCategory, setIsNewCategory] = useState(false);
  const [imageError, setImageError] = useState('');

  useEffect(() => {
    fetchCategories();
    if (isEditing) {
      fetchPortfolioItem();
    }
  }, [isEditing]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/categories');
      console.log('Categories:', response.data);
      setCategories(response.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError(err.response?.data?.message || 'Failed to fetch categories');
    }
  };

  const fetchPortfolioItem = async () => {
    try {
      const response = await axios.get(`/api/portfolio/${id}`);
      console.log('Portfolio item:', response.data);
      const { title, category, imageUrl } = response.data;
      setFormData({
        title,
        categoryId: category?.id || '',
        categoryName: '',
        image: null
      });
      if (imageUrl) {
        setPreview(`${axios.defaults.baseURL}${imageUrl}`);
      }
    } catch (err) {
      console.error('Error fetching portfolio item:', err);
      setError(err.response?.data?.message || 'Failed to fetch portfolio item');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImageError('');

    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        setImageError('Please upload a valid image file (JPEG, PNG, or GIF)');
        return;
      }

      setFormData({ ...formData, image: file });
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setImageError('');

    // Validate image for new items
    if (!isEditing && !formData.image) {
      setImageError('Please select an image');
      setLoading(false);
      return;
    }

    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);
      
      if (isNewCategory && formData.categoryName) {
        submitData.append('categoryName', formData.categoryName);
      } else if (formData.categoryId) {
        submitData.append('categoryId', formData.categoryId);
      }

      // Append image as 'image' field for multer
      if (formData.image) {
        submitData.append('image', formData.image);
      }

      // Send with proper headers for multipart/form-data
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      if (isEditing) {
        await axios.patch(`/api/portfolio/${id}`, submitData, config);
      } else {
        await axios.post('/api/portfolio', submitData, config);
      }
      navigate('/admin/portfolio');
    } catch (err) {
      console.error('Error saving portfolio item:', err);
      setError(err.response?.data?.message || 'Failed to save portfolio item');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">
        {isEditing ? 'Edit Portfolio Item' : 'Add New Portfolio Item'}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Image Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Image {!isEditing && <span className="text-red-500">*</span>}
          </label>
          <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg">
            <div className="space-y-1 text-center">
              {preview ? (
                <div className="relative">
                  <img
                    src={preview}
                    alt="Preview"
                    className="mx-auto h-64 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setPreview(null);
                      setFormData({ ...formData, image: null });
                      setImageError('');
                    }}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <FiX />
                  </button>
                </div>
              ) : (
                <>
                  <FiUpload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-dark">
                      <span>Upload a file</span>
                      <input
                        type="file"
                        className="sr-only"
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">
                    PNG, JPG, GIF files
                  </p>
                </>
              )}
            </div>
          </div>
          {imageError && (
            <p className="mt-2 text-sm text-red-500">{imageError}</p>
          )}
        </div>

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            required
          />
        </div>

        {/* Category Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category <span className="text-red-500">*</span>
          </label>
          <div className="space-y-2">
            <select
              value={isNewCategory ? 'new' : formData.categoryId}
              onChange={(e) => {
                if (e.target.value === 'new') {
                  setIsNewCategory(true);
                  setFormData({ ...formData, categoryId: '', categoryName: '' });
                } else {
                  setIsNewCategory(false);
                  setFormData({ ...formData, categoryId: e.target.value, categoryName: '' });
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              required
            >
              <option value="">Select a category</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
              <option value="new">+ Create new category</option>
            </select>

            {isNewCategory && (
              <input
                type="text"
                placeholder="Enter new category name"
                value={formData.categoryName}
                onChange={(e) =>
                  setFormData({ ...formData, categoryName: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
            )}
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-500 text-sm">{error}</p>
          </div>
        )}

        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/admin/portfolio')}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
          >
            <FiSave />
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PortfolioForm;
